import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Eye, EyeOff, ArrowLeft, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { profileService } from "@/services";
import { apiGetPublic, apiPost, apiPostPublic } from "@/lib/apiClient";
import { z } from "zod";
import logoMark from "@/assets/logo-mark.png";

// Validation schemas
const signInSchema = z.object({
  email: z.string().trim().email("Invalid email address").max(255, "Email too long"),
  password: z.string().min(6, "Password must be at least 6 characters").max(72, "Password too long"),
});

const signUpSchema = signInSchema
  .extend({
    fullName: z.string().trim().min(1, "Full name is required").max(100, "Name too long"),
    confirmPassword: z.string().min(6, "Password must be at least 6 characters"),
    inviteCode: z
      .string()
      .trim()
      .length(6, "Invite code must be 6 characters")
      .regex(/^[A-Z0-9]+$/, "Invalid invite code format"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });
export default function Auth() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [isSignUp, setIsSignUp] = useState(searchParams.get("mode") === "signup");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    fullName: "",
    inviteCode: "",
  });

  // Redirect if already logged in
  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "inviteCode" ? value.toUpperCase() : value,
    }));
  };

  const validateInviteCode = async (code: string): Promise<boolean> => {
    try {
      const response = await apiGetPublic<{ valid: boolean; error?: string }>(
        `/api/invites/${encodeURIComponent(code)}/validate`,
      );
      return response.valid === true;
    } catch (error) {
      console.error("Validate invite exception:", error);
      return false;
    }
  };

  const claimInviteCode = async (code: string, profileId: string): Promise<boolean> => {
    try {
      // claim now requires the user's JWT (server uses auth.uid() to verify
      // the profile_id matches). Caller has a session by this point — they
      // just completed signUp.
      const response = await apiPost<{ success: boolean; error?: string }>(
        `/api/invites/${encodeURIComponent(code)}/claim`,
        { profile_id: profileId },
      );
      return response.success === true;
    } catch (error) {
      console.error("Claim invite exception:", error);
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    // Validate form data with Zod
    try {
      if (isSignUp) {
        signUpSchema.parse({
          email: formData.email,
          password: formData.password,
          confirmPassword: formData.confirmPassword,
          fullName: formData.fullName,
          inviteCode: formData.inviteCode.toUpperCase(),
        });
      } else {
        signInSchema.parse({
          email: formData.email,
          password: formData.password,
        });
      }
    } catch (validationError) {
      if (validationError instanceof z.ZodError) {
        const firstError = validationError.errors[0];
        toast({
          title: "Validation Error",
          description: firstError.message,
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }
    }

    // Validate invite code with edge function for sign-up
    if (isSignUp) {
      const trimmedCode = formData.inviteCode.trim().toUpperCase();
      const isValidInvite = await validateInviteCode(trimmedCode);
      if (!isValidInvite) {
        toast({
          title: "Error",
          description: "Invalid or expired invite code",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }
    }

    try {
      if (isSignUp) {
        const { data, error } = await supabase.auth.signUp({
          email: formData.email,
          password: formData.password,
          options: {
            emailRedirectTo: `${window.location.origin}/dashboard`,
            data: {
              full_name: formData.fullName,
            },
          },
        });

        if (error) throw error;

        // Claim the invite code with the new user's profile ID
        if (data.user?.id) {
          const claimed = await claimInviteCode(formData.inviteCode.trim(), data.user.id);
          if (!claimed) {
            console.warn("Failed to claim invite code, but user was created");
          }
        }

        toast({
          title: "Account Created",
          description: "Please check your email to confirm your account.",
        });
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({
          email: formData.email,
          password: formData.password,
        });

        if (error) {
          // Log failed login attempt
          await logLoginAttempt({
            email: formData.email,
            success: false,
            failure_reason: error.message,
          });
          throw error;
        }

        // Fetch profile to get company_id for the log using service
        let companyId: string | null = null;
        if (data.user?.id) {
          const { data: profile } = await profileService.get(data.user.id);
          companyId = profile?.company_id || null;
        }

        // Log successful login
        await logLoginAttempt({
          email: formData.email,
          success: true,
          user_id: data.user?.id,
          company_id: companyId,
        });

        toast({
          title: "Welcome Back",
          description: "You have been signed in successfully.",
        });
        navigate("/dashboard");
      }
    } catch (error: any) {
      let message = "An error occurred. Please try again.";

      if (error.message?.includes("Invalid login credentials")) {
        message = "Invalid email or password.";
      } else if (error.message?.includes("User already registered")) {
        message = "An account with this email already exists.";
      } else if (error.message?.includes("Email not confirmed")) {
        message = "Please confirm your email address before signing in.";
      } else if (error.message) {
        message = error.message;
      }

      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to log login attempts
  const logLoginAttempt = async (payload: {
    email: string;
    success: boolean;
    user_id?: string;
    company_id?: string | null;
    failure_reason?: string;
  }) => {
    try {
      await apiPostPublic("/api/audit/login", payload);
    } catch (err) {
      // Don't block login flow if logging fails
      console.error("Failed to log login attempt:", err);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Left Panel - Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-sidebar p-12 flex-col justify-between">
        <div>
          <Link to="/" className="flex items-center gap-3 mb-16">
            <img src={logoMark} alt="Prolarus" className="w-9 h-9 rounded-lg" />
            <span className="text-2xl font-semibold text-sidebar-accent-foreground">Prolarus</span>
          </Link>
          <div className="max-w-md">
            <h1 className="text-4xl font-bold text-sidebar-accent-foreground mb-6">
              Streamline Your Invoice Compliance
            </h1>
            <p className="text-lg text-sidebar-foreground">
              Manage vendors, validate invoices, and maintain complete compliance documentation with our powerful
              platform.
            </p>
          </div>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md animate-fade-in">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>

          <div className="mb-8">
            <h2 className="text-3xl font-bold text-foreground mb-2">
              {isSignUp ? "Create your account" : "Welcome back"}
            </h2>
            <p className="text-muted-foreground">
              {isSignUp ? "Enter your invite code to get started" : "Sign in to access your dashboard"}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {isSignUp && (
              <>
                <div>
                  <label htmlFor="inviteCode" className="block text-sm font-medium text-foreground mb-2">
                    Invite Code
                  </label>
                  <input
                    type="text"
                    id="inviteCode"
                    name="inviteCode"
                    value={formData.inviteCode}
                    onChange={handleInputChange}
                    className="input-field uppercase"
                    placeholder="Enter 6-character code"
                    required
                    maxLength={6}
                  />
                </div>

                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-foreground mb-2">
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="input-field"
                    placeholder="John Doe"
                    required
                  />
                </div>
              </>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="input-field"
                placeholder="john@company.com"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-foreground mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="input-field pr-12"
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {isSignUp && (
              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground mb-2">
                  Confirm Password
                </label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="input-field"
                  placeholder="••••••••"
                  required
                  minLength={6}
                />
              </div>
            )}

            <button type="submit" disabled={isLoading} className="btn-primary w-full py-4">
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {isSignUp ? "Creating Account..." : "Signing In..."}
                </>
              ) : isSignUp ? (
                "Create Account"
              ) : (
                "Sign In"
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-muted-foreground">
              {isSignUp ? "Already have an account?" : "Don't have an account?"}{" "}
              <button onClick={() => setIsSignUp(!isSignUp)} className="text-primary hover:underline font-medium">
                {isSignUp ? "Sign in" : "Sign up"}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
